/**
 * SOVRANOVA — Live Companion (Phase 3)
 * Full-duplex voice that runs through the SOVRANOVA brain (memory + care-tier +
 * Claims Firewall + crisis) instead of Hume's built-in LLM. EVI handles the
 * empathic, interruptible turn-taking; every turn is generated by our Custom
 * Language Model endpoint via the "SOVRANOVA Live" config.
 *
 * Security: the browser holds NO secret. /api/live/start mints a short-lived
 * signed token carrying the trusted userId/sessionId; we pass it to Hume as
 * customSessionId, and the CLM endpoint verifies it — so userId can't be forged.
 */
import { useState, useEffect, useRef } from 'react'
import { VoiceProvider, useVoice, VoiceReadyState } from '@humeai/voice-react'
import { api } from '../../services/api'
import { useVoiceSafety, CrisisNotice } from '../../hooks/useVoiceSafety'
import { useWakeLock, useVoiceResume } from '../../hooks/useSessionResilience'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'
import { useSettings } from '../../hooks/useSettings'
import { resolveRegion, getCrisisResources } from '../../services/crisisResources'

const clean = (t) => (t || '').replace(/\[ACTION:\w+\]/g, '').trim()

function toHistory(messages) {
  return messages
    .filter(m => m.type === 'user_message' || m.type === 'assistant_message')
    .map(m => ({ role: m.type === 'user_message' ? 'user' : 'assistant', content: m.message?.content || '' }))
    .filter(m => m.content)
}

// ── Live voice session — connects on mic tap, driven by the CLM config ──────
function LiveConversation({ humeToken, liveToken, configId, sessionId, userName, onClose }) {
  const { connect, disconnect, readyState, messages, micFft, isPlaying, mute, unmute } = useVoice()
  const { settings } = useSettings()
  const echoSafeMode = !!settings.echoSafeMode
  const crisis = useVoiceSafety(messages)
  const [connected,  setConnected]  = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error,      setError]      = useState(null)
  const bottomRef = useRef(null)
  const msgRef = useRef([]); msgRef.current = messages

  // Persist episodic + procedural memory when the Live session ends.
  const endSession = () => {
    try {
      const conversationHistory = toHistory(msgRef.current)
      if (sessionId && conversationHistory.length >= 2) {
        api.post('/session/end', { sessionId, conversationHistory, agentUsed: 'LIVE' }).catch(() => {})
      }
    } catch { /* memory persist is best-effort */ }
  }
  useEffect(() => () => { endSession(); disconnect() }, [])

  // BARGE-IN (28 Aug, David: "when I speak, she pauses and still listens —
  // identical to ChatGPT"). The mic now stays OPEN while she speaks, so EVI's
  // native interruption fires the moment you talk over her and playback stops.
  // This relies on the browser's echo cancellation (on by default in every
  // modern browser) plus a server-side echo veto in humeClm.js that silently
  // drops any "user message" that is just her own recent words re-captured.
  // ECHO-SAFE MODE (Settings) restores the old Wave-1 guard — mic muted while
  // assistant audio plays, unmute +400ms — for speakerphone setups where AEC
  // isn't enough and she starts answering herself (the ae0c87d bug).
  useEffect(() => {
    if (!connected) return
    if (!echoSafeMode) { try { unmute() } catch (_) {}; return }
    if (isPlaying) { try { mute() } catch (_) {}; return }
    const t = setTimeout(() => { try { unmute() } catch (_) {} }, 400)
    return () => clearTimeout(t)
  }, [isPlaying, connected, echoSafeMode])

  // Mic activation. Prime getUserMedia inside the tap (satisfies the browser
  // gesture rule + iOS/Edge teardown race), hold it open through connect(),
  // then release once Hume has opened its own stream.
  const startMic = async () => {
    if (connecting || connected) return
    setConnecting(true); setError(null)
    let primeStream = null
    try { primeStream = await navigator.mediaDevices.getUserMedia({ audio: true }) }
    catch (e) {
      setConnecting(false)
      setError(e?.name === 'NotAllowedError' || e?.message?.includes('Permission')
        ? 'Microphone is blocked. Allow it in your browser settings, then tap again.'
        : `Could not start the microphone (${e?.name || 'error'}). Tap to try again.`)
      return
    }
    try {
      await openSocket(primeStream)
      setConnected(true); setConnecting(false)
    } catch (e) {
      setConnecting(false)
      setError(`Could not start the microphone (${e?.name || 'connect'}). Tap to try again.`)
    } finally { primeStream?.getTracks().forEach(t => t.stop()) }
  }

  // Open the EVI socket. Shared by first connect and by resume-after-sleep, so
  // there is exactly one definition of what a Live connection is.
  const openSocket = async (primedStream = null) => {
    // Prime the mic FIRST, synchronously inside the tap that called us — on
    // iOS, awaiting the token fetch first consumes the gesture window, so the
    // resume tap silently failed (same bug as Vent, Wave 1, 20 Aug).
    // Latency (28 Aug, part of the measured 9.5s tap-to-greeting): the old
    // chain here was mic AGAIN (startMic had already primed it) → token fetch
    // → connect, all serial. Now: first connect reuses startMic's stream so
    // the mic is requested exactly once, and the token refresh (still needed —
    // Hume tokens are short-lived and this path also serves resume-after-
    // sleep) runs IN PARALLEL with any mic prime instead of after it. The
    // getUserMedia CALL still happens synchronously inside the gesture.
    const micP = primedStream
      ? Promise.resolve(null)
      : navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null)
    const tokenP = api.post('/hume/token', { purpose: 'live' }).catch(() => null)
    const [ownStream, r] = await Promise.all([micP, tokenP])
    const token = r?.accessToken || humeToken

    try {
      await connect({
        auth: { type: 'accessToken', value: token },
        configId,
        sessionSettings: { type: 'session_settings', customSessionId: liveToken },
      })
    } finally {
      ownStream?.getTracks().forEach(t => t.stop())
    }
  }

  // Keep the screen awake while talking — a still moment shouldn't end the
  // session — and recover the socket if the phone slept anyway.
  useWakeLock(connected)
  const { suspended, resuming, resume } = useVoiceResume({
    active: connected,
    isOpen: () => readyState === VoiceReadyState.OPEN,
    reconnect: async () => { await openSocket() },
  })

  useChatAutoScroll(bottomRef, messages)   // follow only while the reader is at the bottom

  const rawChat = messages.filter(m => m.type === 'user_message' || m.type === 'assistant_message')
  // One bubble per turn (28 Aug, from David's screenshots): Hume emits an
  // assistant_message PER SPOKEN CHUNK, so a streamed reply rendered event-by-
  // event shows as several bubbles — and a superseded fragment reply shows as
  // a near-duplicate of the fuller one that follows. Merge consecutive
  // same-role messages: if the new text extends the old (whitespace-insensitive
  // prefix), keep the fuller version; otherwise append. User re-fires merge the
  // same way, mirroring the server-side transcript merge.
  const chat = []
  for (const m of rawChat) {
    const isUser = m.type === 'user_message'
    const text = clean(m.message?.content)
    if (!text) continue
    const prev = chat[chat.length - 1]
    if (prev && prev.isUser === isUser) {
      const squash = s => s.replace(/\s+/g, '').toLowerCase()
      const a = squash(prev.text), b = squash(text)
      if (b.startsWith(a) || a.startsWith(b)) prev.text = b.length >= a.length ? text : prev.text
      else prev.text = `${prev.text} ${text}`
    } else {
      chat.push({ isUser, text })
    }
  }
  const isConnecting = readyState === VoiceReadyState.CONNECTING || connecting

  return (
    <div style={c.panel}>
      <div style={c.header}>
        <div style={c.dot(connected, isConnecting)} />
        <span style={c.title}>
          {suspended ? 'Paused — tap to resume'
            : connected ? `Listening${userName ? `, ${userName}` : ''}…`
            : isConnecting ? 'Connecting…' : 'SOVRANOVA Live'}
        </span>
        <button style={c.backOnDark} onClick={() => { disconnect(); onClose() }}>&#8592; Back</button>
      </div>

      <div style={c.scroll}>
        {chat.length === 0 && (
          <p style={c.idle}>
            {connected
              ? `I'm here${userName ? `, ${userName}` : ''} — just speak freely.`
              : isConnecting ? 'Connecting to voice…' : 'One tap and I’m listening.'}
          </p>
        )}
        {chat.map((m, i) => (
          <div key={i} style={{ display:'flex', justifyContent: m.isUser ? 'flex-end' : 'flex-start', marginBottom:'0.4rem' }}>
            <div style={{ ...c.bubble, ...(m.isUser ? c.user : c.ai) }}>{m.text}</div>
          </div>
        ))}
        <CrisisNotice crisis={crisis} />
        <div ref={bottomRef} />
      </div>

      {connected && micFft && (
        <div style={c.fftRow}>
          {micFft.slice(0, 18).map((v, i) => (
            <div key={i} style={{ ...c.fft, height: `${3 + (v||0) * 26}px`, opacity: 0.35 + (v||0) * 0.65 }} />
          ))}
        </div>
      )}

      {error && <p style={c.err}>{error}</p>}

      {connected && suspended && (
        <button style={c.mic} onClick={resume} disabled={resuming}>
          {resuming ? 'Reconnecting…' : '🎙 Tap to pick up where we left off'}
        </button>
      )}

      {!connected && (
        <button style={c.mic} onClick={startMic} disabled={isConnecting}>
          {isConnecting ? 'Connecting…' : error ? '🎙 Tap to try again' : '🎙 Tap to begin'}
        </button>
      )}

      <p style={c.hint}>
        {connected
          ? 'Speak freely — I hear how you feel, not just what you say · Hume EVI 3 × SOVRANOVA'
          : 'One tap to begin — then it stays hands-free'}
      </p>
    </div>
  )
}

// ── Entry point: fetch Hume token + start a Live session, then open on tap ──
export function LiveCompanionView({ userName, onClose }) {
  const [phase, setPhase]     = useState('loading')  // loading | ready | unavailable | error
  const [humeToken, setHume]  = useState(null)
  const [live, setLive]       = useState(null)       // { token, configId, sessionId, careTier }
  const [active, setActive]   = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.post('/hume/token', { purpose: 'live' }).then(r => r.accessToken).catch(() => null),
      // Region rides in the signed token so every Live crisis reply uses THIS
      // person's resources (unknown → GLOBAL), never a default country.
      api.post('/live/start', { sessionType: 'REFLECTION', region: resolveRegion() }).catch(e => ({ _err: e })),
    ]).then(([token, ls]) => {
      if (cancelled) return
      if (!token) return setPhase('error')
      if (!ls || ls._err || !ls.token || !ls.configId) return setPhase('unavailable')
      setHume(token); setLive(ls); setPhase('ready')
    })
    return () => { cancelled = true }
  }, [])

  if (active && humeToken && live) {
    return (
      <VoiceProvider>
        <LiveConversation
          humeToken={humeToken} liveToken={live.token} configId={live.configId}
          sessionId={live.sessionId} userName={userName}
          onClose={() => setActive(false)} // back = one step: leave the room, return to the intro card
        />
      </VoiceProvider>
    )
  }

  const tier = live?.careTier
  const careRes = getCrisisResources(resolveRegion())
  return (
    <div style={c.card}>
      <div style={c.header}>
        <div style={c.orb} />
        <span style={c.label}>SOVRANOVA Live</span>
        <button style={c.backBtn} onClick={() => onClose?.()}>&#8592; Back</button>
      </div>

      <p style={c.blurb}>
        A hands-free voice companion that hears how you feel and remembers your journey.
        {userName ? ` Whenever you’re ready, ${userName}.` : ''}
      </p>

      {(tier === 'amber' || tier === 'red') && (
        <p style={c.careNote}>
          I’ll keep this gentle and go at your pace. If things feel heavy we can slow right down —
          and real human support is always here: {careRes.primary.label} {careRes.primary.number}, or {careRes.emergency.number} in an emergency.
        </p>
      )}

      {phase === 'loading' && <div style={c.await}><span style={c.awaitDot}/> <span style={c.awaitText}>Preparing your space…</span></div>}
      {phase === 'ready' && <button style={c.start} onClick={() => setActive(true)}>🎙 Start Live</button>}
      {phase === 'unavailable' && <p style={c.note}>Live voice isn’t switched on yet. You can still use Session, Vent, and Guided sessions.</p>}
      {phase === 'error' && <p style={c.note}>Voice couldn’t start just now. Please try again in a moment.</p>}

      <p style={c.foot}>One tap to begin — then it stays hands-free · Hume EVI 3 × SOVRANOVA brain</p>
    </div>
  )
}

// ── Styles ──────────────────────────────────────────────────────────────────
// Organic restyle (Board 3 "L1", 2026-08-13): cream intro card, terracotta-led
// "quiet room" — deep warm ink with a soft terracotta glow, matching the
// companion's live panel. Values only; structure and behavior untouched.
const c = {
  card:  { background:'var(--og-n100, #f9f4ed)', borderRadius:'var(--og-rl, 28px)', border:'none', boxShadow:'none', padding:'1.4rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.8rem' },
  orb:   { width:8, height:8, borderRadius:'50%', background:'var(--og-accent, #c67139)', flexShrink:0 },
  label: { flex:1, fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--og-a700, #8c491a)' },
  blurb: { fontSize:'1rem', color:'var(--og-n800, #474238)', lineHeight:1.65, margin:0 },
  careNote:{ fontSize:'0.8rem', color:'var(--og-a800, #643312)', background:'var(--og-a100, #fff2eb)', border:'none', borderRadius:'16px', padding:'0.75rem 0.9rem', lineHeight:1.5, margin:0 },
  note:  { fontSize:'0.82rem', color:'var(--og-n700, #645c50)', lineHeight:1.5, margin:0 },
  start: { padding:'0.95rem 1rem', width:'100%', background:'var(--og-accent, #c67139)', color:'var(--og-bg, #f5ead8)', border:'none', borderRadius:'999px', fontSize:'1rem', fontWeight:400, cursor:'pointer', fontFamily:'var(--og-fh, Georgia, serif)' },
  foot:  { fontSize:'0.66rem', color:'var(--og-n500, #a19786)', textAlign:'center', letterSpacing:'0.03em' },
  await: { display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.6rem' },
  awaitDot:{ width:8, height:8, borderRadius:'50%', background:'var(--og-accent, #c67139)', animation:'svLive 1.2s infinite' },
  awaitText:{ fontSize:'0.84rem', color:'var(--og-a700, #8c491a)', fontWeight:600 },
  panel: { background:'radial-gradient(circle at 50% 118%, rgba(198,113,57,.28), transparent 62%), var(--og-n900, #2e2b25)', borderRadius:'var(--og-rl, 28px)', padding:'1.3rem 1.4rem', display:'flex', flexDirection:'column', gap:'0.7rem', boxShadow:'var(--og-shadow, 0 3px 10px rgba(46,43,37,0.16))' },
  header:{ display:'flex', alignItems:'center', gap:'0.5rem' },
  x:     { background:'transparent', border:'none', color:'rgba(245,234,216,.45)', fontSize:'1.1rem', cursor:'pointer', lineHeight:1, padding:'0 0.1rem' },
  // Every page gets a clearly visible Back (user request, 2026-08-14) — one on
  // the cream intro card, one legible on the dark room. Back is one step only.
  backBtn:    { background:'transparent', border:'1.5px solid var(--og-n300, #dcd3c4)', color:'var(--og-n700, #645c50)', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.8rem', fontFamily:'inherit', flexShrink:0 },
  backOnDark: { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.8)', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.8rem', fontFamily:'inherit', flexShrink:0 },
  dot: (on, ing) => ({ width:9, height:9, borderRadius:'50%', flexShrink:0, background: on ? '#7a8a5e' : ing ? '#e6a020' : '#666', boxShadow: on ? '0 0 0 4px rgba(122,138,94,.25)' : 'none', animation: on ? 'svLive 2s infinite' : ing ? 'svLive 0.7s infinite' : 'none' }),
  title: { flex:1, fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(245,234,216,.65)' },
  scroll:{ display:'flex', flexDirection:'column', gap:'0.35rem', maxHeight:150, overflowY:'auto' },
  idle:  { color:'rgba(245,234,216,.55)', fontSize:'0.92rem', lineHeight:1.6, textAlign:'center', padding:'0.5rem 0' },
  bubble:{ maxWidth:'86%', padding:'0.6rem 0.9rem', borderRadius:'18px', fontSize:'0.9rem', lineHeight:1.55 },
  user:  { background:'rgba(198,113,57,.35)', color:'#f5ead8', borderBottomRightRadius:'6px', marginLeft:'auto' },
  ai:    { background:'rgba(122,138,94,.35)', color:'#f0fae1', borderBottomLeftRadius:'6px' },
  fftRow:{ display:'flex', alignItems:'center', gap:3, height:28, justifyContent:'center' },
  fft:   { width:4, borderRadius:3, background:'rgba(255,198,165,0.85)', transition:'height 0.05s' },
  mic:   { padding:'0.95rem 1rem', width:'100%', background:'var(--og-accent, #c67139)', color:'var(--og-bg, #f5ead8)', border:'none', borderRadius:'999px', fontSize:'0.98rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit', boxShadow:'none' },
  err:   { fontSize:'0.75rem', color:'#ffc6a5', textAlign:'center', lineHeight:1.5 },
  hint:  { fontSize:'0.64rem', color:'rgba(245,234,216,.38)', textAlign:'center', letterSpacing:'0.03em' },
}

if (typeof document !== 'undefined' && !document.getElementById('sv-live-anim')) {
  const el = document.createElement('style'); el.id = 'sv-live-anim'
  el.textContent = '@keyframes svLive{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.5)}}'
  document.head.appendChild(el)
}
